import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAppDispatch } from '../hooks/redux'
import { loginSuccess } from '../store/slices/authSlice'
import { useCompatRouter } from '../hooks/useCompatRouter'
import { registerPortalGuest } from '../services/guestPortalSupabase'

interface RegisterFormData {
  firstName: string
  lastName: string
  email: string
  password: string
  confirmPassword: string
}

const RegisterPage = () => {
  const [formData, setFormData] = useState<RegisterFormData>({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
  })
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [message, setMessage] = useState('')
  const [shouldCloseAfterVerifyNotice, setShouldCloseAfterVerifyNotice] = useState(false)

  const dispatch = useAppDispatch()
  const router = useCompatRouter()

  useEffect(() => {
    if (!shouldCloseAfterVerifyNotice) {
      return
    }

    const timeoutId = window.setTimeout(() => {
      window.close()

      window.setTimeout(() => {
        if (!window.closed) {
          void router.replace('/booking-system/login')
        }
      }, 250)
    }, 1800)

    return () => {
      window.clearTimeout(timeoutId)
    }
  }, [router, shouldCloseAfterVerifyNotice])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
    setError('')
    setMessage('')
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setMessage('')

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match.')
      return
    }
    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters.')
      return
    }

    setIsLoading(true)
    try {
      const sessionState = await registerPortalGuest({
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        email: formData.email.trim(),
        password: formData.password,
      })

      dispatch(loginSuccess(sessionState))

      await router.push('/booking-system/dashboard')
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Registration failed. Please try again.'

      if (/registration submitted/i.test(errorMessage) || /check your email to confirm/i.test(errorMessage)) {
        setMessage('Registration submitted. Please verify your email address to activate your account. This page will close automatically.')
        setShouldCloseAfterVerifyNotice(true)
        return
      }

      setError(errorMessage)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="admin-login-theme min-h-screen px-4 py-10 sm:px-6 lg:px-8">
      <div className="mx-auto flex min-h-[calc(100vh-5rem)] max-w-6xl items-center justify-center">
        <div className="admin-login-panel">
          <aside className="admin-login-aside">
            <div>
              <span className="admin-login-kicker">Guest Account</span>
              <h1 className="text-4xl font-bold leading-tight text-white">Create your Marina Glen account and manage your stays.</h1>
              <p className="mt-4 max-w-md text-base text-white/80">
                Register to view your bookings, manage your profile, and enjoy a seamless check-in experience.
              </p>
              <ul className="admin-login-feature-list">
                <li>View and manage your bookings</li>
                <li>Quick check-in with saved details</li>
                <li>Booking history and receipts</li>
              </ul>
            </div>
            <div className="text-sm text-white/70">
              <p>Already have an account?</p>
              <Link to="/booking-system/login" className="mt-2 inline-flex text-sm font-semibold text-white hover:text-white/80 transition-colors">
                Sign in here
              </Link>
            </div>
          </aside>

          <section className="admin-login-content">
            <div className="mb-8">
              <p className="text-sm font-semibold uppercase tracking-[0.18em] text-slate-500">New Account</p>
              <h2 className="mt-3 text-4xl font-bold text-black">Create Account</h2>
              <p className="mt-3 text-base text-slate-600">Join Marina Glen Holiday Resort.</p>
            </div>

            {error && (
              <div className="mb-5 rounded-2xl border border-red-200 bg-red-50 px-4 py-3">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            {message && (
              <div className="mb-5 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3">
                <p className="text-sm text-emerald-700">{message}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="firstName" className="mb-2 block text-sm font-semibold text-slate-700">
                    First Name
                  </label>
                  <input
                    id="firstName"
                    name="firstName"
                    type="text"
                    required
                    value={formData.firstName}
                    onChange={handleInputChange}
                    className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3.5 text-slate-900 shadow-sm transition-all duration-300 focus:border-sky-500 focus:outline-none focus:ring-4 focus:ring-sky-100"
                    placeholder="First name"
                  />
                </div>
                <div>
                  <label htmlFor="lastName" className="mb-2 block text-sm font-semibold text-slate-700">
                    Last Name
                  </label>
                  <input
                    id="lastName"
                    name="lastName"
                    type="text"
                    required
                    value={formData.lastName}
                    onChange={handleInputChange}
                    className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3.5 text-slate-900 shadow-sm transition-all duration-300 focus:border-sky-500 focus:outline-none focus:ring-4 focus:ring-sky-100"
                    placeholder="Last name"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="email" className="mb-2 block text-sm font-semibold text-slate-700">
                  Email Address
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3.5 text-slate-900 shadow-sm transition-all duration-300 focus:border-sky-500 focus:outline-none focus:ring-4 focus:ring-sky-100"
                  placeholder="Enter your email"
                />
              </div>

              <div>
                <label htmlFor="password" className="mb-2 block text-sm font-semibold text-slate-700">
                  Password
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  required
                  value={formData.password}
                  onChange={handleInputChange}
                  className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3.5 text-slate-900 shadow-sm transition-all duration-300 focus:border-sky-500 focus:outline-none focus:ring-4 focus:ring-sky-100"
                  placeholder="At least 6 characters"
                />
              </div>

              <div>
                <label htmlFor="confirmPassword" className="mb-2 block text-sm font-semibold text-slate-700">
                  Confirm Password
                </label>
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  required
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3.5 text-slate-900 shadow-sm transition-all duration-300 focus:border-sky-500 focus:outline-none focus:ring-4 focus:ring-sky-100"
                  placeholder="Repeat your password"
                />
              </div>

              <button
                type="submit"
                disabled={isLoading || shouldCloseAfterVerifyNotice}
                className="w-full rounded-2xl bg-slate-950 px-4 py-3.5 font-semibold text-white transition-all duration-300 hover:-translate-y-0.5 hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isLoading ? 'Creating Account...' : 'Create Account'}
              </button>
            </form>

            <div className="mt-8 flex flex-col gap-3 text-sm text-slate-600 sm:flex-row sm:items-center sm:justify-between">
              <p>
                Already have an account?{' '}
                <Link to="/booking-system/login" className="font-semibold text-sky-700 hover:text-sky-800">
                  Sign in
                </Link>
              </p>
              <Link
                to="/booking-system"
                className="font-medium text-slate-500 transition-colors hover:text-slate-800"
              >
                ← Back to Home
              </Link>
            </div>
          </section>
        </div>
      </div>
    </div>
  )
}

export default RegisterPage
